import { Capacitor } from '@capacitor/core';
import { platformType } from '@et/typings';
import { environment } from '@et/environment';

export type NativeAuthProvider = 'betterAuth' | 'aad';

const NATIVE_AUTH_PROVIDER_SESSION_KEY = 'ET_NATIVE_AUTH_PROVIDER';

/**
 * Selects the authentication provider for the current shell.
 *
 * Web and Electron continue to use the workspace-wide `useBetterAuth` flag.
 * iOS and Android prefer the session value written from Profile
 * `GET /public/auth`, then `useBetterAuthNative`.
 */
export function useBetterAuthForCurrentPlatform(): boolean {
  const platform = Capacitor.getPlatform();
  if (platform === platformType.IOS || platform === platformType.ANDROID) {
    const selected = getStoredNativeAuthProvider();
    if (selected) {
      return selected === 'betterAuth';
    }
    return environment.useBetterAuthNative;
  }
  return environment.useBetterAuth;
}

export function getStoredNativeAuthProvider(): NativeAuthProvider | null {
  try {
    const selected = sessionStorage.getItem(NATIVE_AUTH_PROVIDER_SESSION_KEY);
    if (selected === 'betterAuth' || selected === 'aad') {
      return selected;
    }
  } catch {
    // Fall back to the build-time default when session storage is unavailable.
  }
  return null;
}

export function setNativeAuthProvider(provider: NativeAuthProvider): void {
  try {
    sessionStorage.setItem(NATIVE_AUTH_PROVIDER_SESSION_KEY, provider);
  } catch {
    // Ignore unavailable session storage (e.g. private mode).
  }
}

/**
 * Maps Profile `GET /public/auth` `{ auth }` onto the native provider union.
 * Unknown values return null so the build-time native flag stays in effect.
 */
export function mapPublicAuthToNativeProvider(
  advertised: string | null | undefined,
): NativeAuthProvider | null {
  if (typeof advertised !== 'string') {
    return null;
  }
  const normalized = advertised
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');
  if (normalized === 'betterauth') {
    return 'betterAuth';
  }
  if (normalized === 'aad') {
    return 'aad';
  }
  return null;
}

/**
 * Applies a public-auth probe result as the native provider for this session.
 */
export function applyPublicAuthProviderFromProbe(
  advertised: string | null | undefined,
): NativeAuthProvider | null {
  const mapped = mapPublicAuthToNativeProvider(advertised);
  if (!mapped) {
    return null;
  }
  setNativeAuthProvider(mapped);
  return mapped;
}
